import {
  Box,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomButtonComponent } from "../../../shared/elements/cutomBtn.component";
import { CustomDialogComponent } from "../../../shared/elements/customDialog.component";
import { CustomInputComponent } from "../../../shared/elements/customInputs.component";
import {
  CustomTableComponent,
  Column,
  getReservationStatus,
} from "../../../shared/elements/customTable.component";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  ItemReservation,
  ItemReview,
  ItemType,
  UpdateItemDetailsDto,
} from "../../ads/dto/ads.dto";
import {
  useDeleteItemDetailsHook,
  useGetAdByIdHook,
  useUpdateItemDetailsHook,
} from "../../ads/hooks/ads.hook";
import { useEventsStore } from "../../../shared/stores/events.store";
import { formatDateTime, formatDuration } from "../../../shared/utils/format.util";
import { DEFAULT_EVENT_IMAGE } from "../../../shared/constants/default-image";

type SortDir = "asc" | "desc";
type ReservationSortKey = "date" | "price" | "status";

const EDITABLE_FIELDS_BY_TYPE: Record<ItemType, string[]> = {
  activity: [
    "price",
    "distance",
    "duration",
    "level",
    "participantNumber",
    "prerequisites",
    "phone",
    "email",
    "website",
  ],
  event: [
    "discipline",
    "price",
    "distance",
    "duration",
    "level",
    "participantNumber",
    "prerequisites",
    "phone",
    "email",
    "website",
  ],
  competition: [
    "discipline",
    "price",
    "distance",
    "duration",
    "level",
    "participantNumber",
    "prerequisites",
    "phone",
    "email",
    "website",
  ],
  restaurant: ["phone", "email", "website"],
  accomodation: ["price", "distance", "participantNumber"],
};

const isFieldEditable = (type: ItemType, field: string) =>
  EDITABLE_FIELDS_BY_TYPE[type].includes(field);

export interface HosterAdsDetailsProps {
  id: string;
  activityName?: string;
  onBack: () => void;
}

const showValue = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === "" ? "—" : value;

const showList = (values: string[]) =>
  values.length > 0 ? values.join(", ") : "—";

const getMapEmbedSrc = (lng: number, lat: number) => {
  const delta = 0.01;
  const bbox = `${lng - delta}%2C${lat - delta}%2C${lng + delta}%2C${
    lat + delta
  }`;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lng}`;
};

export const HosterAdsDetailsComponent: React.FC<HosterAdsDetailsProps> = ({
  id,
  activityName,
  onBack,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const { event } = useEventsStore();
  const { data, isLoading, error } = useGetAdByIdHook(id, event);
  const updateItemMutation = useUpdateItemDetailsHook();
  const deleteItemMutation = useDeleteItemDetailsHook();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleConfirmDelete = () => {
    if (deleteItemMutation.isPending) return;
    setDeleteError(null);
    deleteItemMutation.mutate(
      { id, event },
      {
        onSuccess: () => {
          setDeleteOpen(false);
          onBack();
        },
        onError: (err: any) =>
          setDeleteError(
            err?.response?.data?.message ||
              "Une erreur est survenue lors de la suppression."
          ),
      }
    );
  };

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    discipline: "",
    price: "",
    distance: "",
    duration: "",
    level: "",
    participantNumber: "",
    prerequisites: "",
    description: "",
    street: "",
    postalCode: "",
    city: "",
    country: "",
    phone: "",
    email: "",
    website: "",
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleEditChange =
    (field: keyof typeof editForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setEditForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const openEdit = () => {
    if (!data) return;
    setEditForm({
      title: data.title || "",
      discipline: data.discipline || "",
      price: data.price != null ? String(data.price) : "",
      distance: data.distance != null ? String(data.distance) : "",
      duration: data.duration || "",
      level: data.level || "",
      participantNumber:
        data.participantNumber != null ? String(data.participantNumber) : "",
      prerequisites: data.prerequisites || "",
      description: data.description || "",
      street: data.street || "",
      postalCode: data.postalCode != null ? String(data.postalCode) : "",
      city: data.city || "",
      country: data.country || "",
      phone: data.phone || "",
      email: data.email || "",
      website: data.website || "",
    });
    setSaveError(null);
    setEditOpen(true);
  };

  const handleSaveEdit = () => {
    if (!data || updateItemMutation.isPending) return;
    const type = data.type;
    const payload: UpdateItemDetailsDto = {
      title: editForm.title,
      description: editForm.description,
      street: editForm.street,
      postalCode: editForm.postalCode,
      city: editForm.city,
      country: editForm.country,
    };
    if (isFieldEditable(type, "discipline")) payload.discipline = editForm.discipline;
    if (isFieldEditable(type, "price"))
      payload.price = editForm.price ? Number(editForm.price) : undefined;
    if (isFieldEditable(type, "distance")) payload.distance = editForm.distance;
    if (isFieldEditable(type, "duration")) payload.duration = editForm.duration;
    if (isFieldEditable(type, "level")) payload.level = editForm.level;
    if (isFieldEditable(type, "participantNumber"))
      payload.participantNumber = editForm.participantNumber
        ? Number(editForm.participantNumber)
        : undefined;
    if (isFieldEditable(type, "prerequisites"))
      payload.prerequisites = editForm.prerequisites;
    if (isFieldEditable(type, "phone")) payload.phone = editForm.phone;
    if (isFieldEditable(type, "email")) payload.email = editForm.email;
    if (isFieldEditable(type, "website")) payload.website = editForm.website;

    updateItemMutation.mutate(
      { id, event, data: payload },
      {
        onSuccess: () => setEditOpen(false),
        onError: () =>
          setSaveError("Une erreur est survenue lors de l'enregistrement."),
      }
    );
  };

  const [reservationSort, setReservationSort] = useState<{
    key: ReservationSortKey;
    dir: SortDir;
  } | null>(null);
  const [reviewSortDir, setReviewSortDir] = useState<SortDir | null>(null);

  const toggleReservationSort = (key: ReservationSortKey) => {
    setReservationSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const toggleReviewSort = () => {
    setReviewSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const sortedReservations = useMemo(() => {
    const rows = (data?.reservations || []).map((r: ItemReservation) => {
      const status = getReservationStatus(r.status);
      return {
        id: r.id,
        clientName: r.clientName,
        clientEmail: r.clientEmail,
        dateTimeDisplay: formatDateTime(r.date),
        dateRaw: r.date,
        duration: formatDuration(r.fromHour, r.toHour),
        price: r.price,
        statusLabel: status.label,
        statusColor: status.color,
      };
    });
    if (!reservationSort) return rows;
    const { key, dir } = reservationSort;
    const sign = dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (key === "date")
        return (
          (new Date(a.dateRaw).getTime() - new Date(b.dateRaw).getTime()) *
          sign
        );
      if (key === "price") return (a.price - b.price) * sign;
      return a.statusLabel.localeCompare(b.statusLabel) * sign;
    });
  }, [data?.reservations, reservationSort]);

  const sortedReviews = useMemo(() => {
    const rows = (data?.reviews || []).map((r: ItemReview) => ({
      id: r.id,
      clientName: r.clientName,
      clientEmail: r.clientEmail,
      rating: r.rating,
      comment: r.comment,
    }));
    if (!reviewSortDir) return rows;
    const sign = reviewSortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => (a.rating - b.rating) * sign);
  }, [data?.reviews, reviewSortDir]);

  const clientColumn: Column = {
    key: "client",
    label: "Client",
    render: (_, row) => (
      <Box>
        <Typography fontWeight="600" fontSize="0.8rem">
          {row.clientName}
        </Typography>
        <Typography fontSize="0.7rem" color="gray">
          {row.clientEmail}
        </Typography>
      </Box>
    ),
  };

  const reservationColumns: Column[] = [
    clientColumn,
    {
      key: "dateTimeDisplay",
      label: "Date et heure ↑↓",
      sortable: true,
      onSort: () => toggleReservationSort("date"),
    },
    { key: "duration", label: "Durée" },
    {
      key: "price",
      label: "Prix ↑↓",
      sortable: true,
      onSort: () => toggleReservationSort("price"),
      render: (value) => `${value} €`,
    },
    {
      key: "statusLabel",
      label: "Status ↑↓",
      sortable: true,
      onSort: () => toggleReservationSort("status"),
      render: (value, row) => (
        <Typography fontWeight="600" sx={{ color: row.statusColor }}>
          {value}
        </Typography>
      ),
    },
  ];

  const reviewColumns: Column[] = [
    clientColumn,
    {
      key: "rating",
      label: "Note ↑↓",
      sortable: true,
      onSort: toggleReviewSort,
      render: (value) => <Typography fontWeight="600">{value}</Typography>,
    },
    {
      key: "comment",
      label: "Commentaire",
      render: (value) => (
        <Typography
          fontSize="0.8rem"
          sx={{
            maxWidth: 260,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </Typography>
      ),
    },
    {
      key: "action",
      label: "",
      render: () => (
        <CustomButtonComponent
          label="Masquer"
          onClick={() => {}}
          disabled
          sx={{
            width: "100px",
            padding: "4px 16px",
            fontSize: "12px",
            background: "transparent",
            border: "2px solid #B4006E",
            color: "#B4006E",
          }}
        />
      ),
    },
  ];

  const handleTabChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: number
  ) => {
    if (newValue !== null) {
      setActiveTab(newValue);
    }
  };

  const title = data?.title || activityName || "Détails";

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={1.5}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={onBack} size="small">
            <ArrowBackIcon
              sx={{
                color: "#B4006E",
              }}
            />
          </IconButton>
          <Typography fontSize="1.15rem" fontWeight="bold" color="#B4006E">
            {title}
          </Typography>
        </Box>
        <CustomButtonComponent
          label="Modifier"
          onClick={openEdit}
          sx={{
            width: "120px",
            padding: "6px 18px",
            fontSize: "13px",
          }}
        />
      </Box>

      {/* Tabs */}
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#FFFDF7",
          borderRadius: "10px",
          display: "flex",
          justifyContent: "center",
          mb: 2,
        }}
      >
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          onChange={handleTabChange}
          aria-label="tabs"
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-around",
          }}
        >
          {["Information", "Réservations", "Avis"].map((label, index) => (
            <ToggleButton
              key={label}
              value={index}
              sx={{
                textTransform: "none",
                fontSize: "13px",
                fontWeight: "600",
                width: "100%",
                padding: "6px 16px",
                border: "none",
                borderRadius: "8px !important",
                color: activeTab === index ? "#fff" : "#555",
                background:
                  activeTab === index
                    ? "linear-gradient(90deg, #B4006E, #FF008D)"
                    : "transparent",
                transition: "0.25s ease",
                "&.Mui-selected": {
                  color: "#fff",
                  background: "linear-gradient(90deg, #B4006E, #FF008D)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #B4006E, #FF008D)",
                  },
                },
                "&:hover": {
                  background: activeTab === index ? "" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <>
          {isLoading && <Box p={2}>Loading...</Box>}
          {(error || (!isLoading && !data)) && (
            <Box p={2}>Error loading item details</Box>
          )}
          {data && (
            <Box>
              {/* Item Details Grid */}
              <Box
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                gap={1.5}
                mb={2}
                p={2}
                borderRadius="12px"
                sx={{
                  backgroundColor: "#FFFDF7",
                }}
              >
                {/* Left Column */}
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {data.type === "activity" && (
                    <Box display="flex" gap={1.5}>
                      <Typography
                        fontWeight="600"
                        fontSize="0.8rem"
                        sx={{ minWidth: "140px" }}
                      >
                        Type d'activité:
                      </Typography>
                      <Typography fontSize="0.8rem">
                        {showValue(data.activityType)}
                      </Typography>
                    </Box>
                  )}
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "140px" }}
                    >
                      Nom :
                    </Typography>
                    <Typography fontSize="0.8rem">{data.title}</Typography>
                  </Box>
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "140px" }}
                    >
                      Discipline:
                    </Typography>
                    <Typography fontSize="0.8rem">
                      {showValue(data.discipline)}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "140px" }}
                    >
                      Prix :
                    </Typography>
                    <Typography fontSize="0.8rem">
                      {data.price != null ? `${data.price}€/personne` : "—"}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "140px" }}
                    >
                      Distance:
                    </Typography>
                    <Typography fontSize="0.8rem">
                      {showValue(data.distance)}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "140px" }}
                    >
                      Durée:
                    </Typography>
                    <Typography fontSize="0.8rem">
                      {showValue(data.duration)}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "140px" }}
                    >
                      Niveau:
                    </Typography>
                    <Typography fontSize="0.8rem">
                      {showValue(data.level)}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "140px" }}
                    >
                      Nombre des participants:
                    </Typography>
                    <Typography fontSize="0.8rem">
                      {showValue(data.participantNumber)}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "140px" }}
                    >
                      Pré-requis:
                    </Typography>
                    <Typography fontSize="0.8rem">
                      {showValue(data.prerequisites)}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "140px" }}
                    >
                      Equipements fournis:
                    </Typography>
                    <Typography fontSize="0.8rem">
                      {showList(data.equipmentsProvided)}
                    </Typography>
                  </Box>
                  {data.type === "activity" && (
                    <Box display="flex" gap={1.5}>
                      <Typography
                        fontWeight="600"
                        fontSize="0.8rem"
                        sx={{ minWidth: "140px" }}
                      >
                        Equipements à prévoir:
                      </Typography>
                      <Typography fontSize="0.8rem">
                        {showList(data.equipmentsToBring)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Right Column */}
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "130px" }}
                    >
                      Adresse:
                    </Typography>
                    <Typography fontSize="0.8rem">{data.street}</Typography>
                  </Box>
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "130px" }}
                    >
                      Code postal:
                    </Typography>
                    <Typography fontSize="0.8rem">{data.postalCode}</Typography>
                  </Box>
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "130px" }}
                    >
                      Ville:
                    </Typography>
                    <Typography fontSize="0.8rem">{data.city}</Typography>
                  </Box>
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "130px" }}
                    >
                      Pays:
                    </Typography>
                    <Typography fontSize="0.8rem">{data.country}</Typography>
                  </Box>

                  {/* Map */}
                  {data.location ? (
                    <Box
                      component="iframe"
                      title="map"
                      src={getMapEmbedSrc(
                        data.location.coordinates[0],
                        data.location.coordinates[1],
                      )}
                      sx={{
                        width: "100%",
                        height: "150px",
                        border: "none",
                        borderRadius: "8px",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "150px",
                        backgroundColor: "#E0E0E0",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <LocationOnIcon sx={{ fontSize: 32, color: "#B4006E" }} />
                    </Box>
                  )}

                  {/* Schedule */}
                  <Box display="flex" flexDirection="column" gap={1} mt={2}>
                    {data.openingHours && data.openingHours.length > 0 ? (
                      data.openingHours.map((slot, index) => (
                        <Box
                          key={`${slot.day}-${index}`}
                          display="flex"
                          gap={1.5}
                        >
                          <Typography
                            fontWeight="600"
                            fontSize="0.8rem"
                            sx={{
                              minWidth: "80px",
                              textTransform: "capitalize",
                            }}
                          >
                            {slot.day} :
                          </Typography>
                          <Typography fontSize="0.8rem">
                            {slot.open} - {slot.close}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography fontSize="0.8rem" color="gray">
                        Horaires non renseignés
                      </Typography>
                    )}
                  </Box>

                  {/* Contact Info */}
                  <Box display="flex" gap={1.5} mt={2}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "130px" }}
                    >
                      Téléphone :
                    </Typography>
                    <Typography fontSize="0.8rem">
                      {showValue(data.phone)}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "130px" }}
                    >
                      Adresse e-mail:
                    </Typography>
                    <Typography fontSize="0.8rem">
                      {showValue(data.email)}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1.5}>
                    <Typography
                      fontWeight="600"
                      fontSize="0.8rem"
                      sx={{ minWidth: "130px" }}
                    >
                      Site web :
                    </Typography>
                    <Typography fontSize="0.8rem">
                      {showValue(data.website)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Description */}
              <Box
                mb={2}
                p={2}
                borderRadius="12px"
                sx={{ backgroundColor: "#FFFDF7" }}
              >
                <Typography fontSize="0.8rem" lineHeight={1.8}>
                  {data.description}
                </Typography>
              </Box>

              {/* Images Gallery */}
              <Box display="flex" gap={1.5} flexWrap="wrap" mb={2}>
                {(data.images.length > 0
                  ? data.images
                  : [DEFAULT_EVENT_IMAGE]
                ).map((image, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: "140px",
                      height: "100px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      backgroundImage: `url(${image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundColor: "#E0E0E0",
                    }}
                  />
                ))}
              </Box>

              {/* Delete link */}
              <Box display="flex" justifyContent="flex-end">
                <Typography
                  onClick={() => setDeleteOpen(true)}
                  sx={{
                    color: "#B4006E",
                    fontWeight: "bold",
                    fontSize: "13px",
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Supprimer
                </Typography>
              </Box>
            </Box>
          )}
        </>
      )}

      {activeTab === 1 && (
        <>
          {isLoading && <Box p={2}>Loading...</Box>}
          {(error || (!isLoading && !data)) && (
            <Box p={2}>Error loading item details</Box>
          )}
          {data && (
            <CustomTableComponent
              columns={reservationColumns}
              rows={sortedReservations}
            />
          )}
        </>
      )}

      {activeTab === 2 && (
        <>
          {isLoading && <Box p={2}>Loading...</Box>}
          {(error || (!isLoading && !data)) && (
            <Box p={2}>Error loading item details</Box>
          )}
          {data && (
            <CustomTableComponent columns={reviewColumns} rows={sortedReviews} />
          )}
        </>
      )}

      {/* Edit Dialog */}
      <CustomDialogComponent
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Modifier l'annonce"
        onConfirm={handleSaveEdit}
        confirmLabel={
          updateItemMutation.isPending ? "Enregistrement..." : "Enregistrer"
        }
        cancelLabel="Annuler"
      >
        <Box display="flex" flexDirection="column" gap={2} py={1}>
          <CustomInputComponent
            label="Nom"
            value={editForm.title}
            onChange={handleEditChange("title")}
            sx={{ width: "100%" }}
          />
          {data && isFieldEditable(data.type, "discipline") && (
            <CustomInputComponent
              label="Discipline"
              value={editForm.discipline}
              onChange={handleEditChange("discipline")}
              sx={{ width: "100%" }}
            />
          )}
          {data && isFieldEditable(data.type, "price") && (
            <CustomInputComponent
              label="Prix"
              type="number"
              value={editForm.price}
              onChange={handleEditChange("price")}
              sx={{ width: "100%" }}
            />
          )}
          {data && isFieldEditable(data.type, "distance") && (
            <CustomInputComponent
              label="Distance"
              value={editForm.distance}
              onChange={handleEditChange("distance")}
              sx={{ width: "100%" }}
            />
          )}
          {data && isFieldEditable(data.type, "duration") && (
            <CustomInputComponent
              label="Durée"
              value={editForm.duration}
              onChange={handleEditChange("duration")}
              sx={{ width: "100%" }}
            />
          )}
          {data && isFieldEditable(data.type, "level") && (
            <CustomInputComponent
              label="Niveau"
              value={editForm.level}
              onChange={handleEditChange("level")}
              sx={{ width: "100%" }}
            />
          )}
          {data && isFieldEditable(data.type, "participantNumber") && (
            <CustomInputComponent
              label="Nombre des participants"
              type="number"
              value={editForm.participantNumber}
              onChange={handleEditChange("participantNumber")}
              sx={{ width: "100%" }}
            />
          )}
          {data && isFieldEditable(data.type, "prerequisites") && (
            <CustomInputComponent
              label="Pré-requis"
              value={editForm.prerequisites}
              onChange={handleEditChange("prerequisites")}
              sx={{ width: "100%" }}
            />
          )}
          <TextField
            label="Description"
            multiline
            minRows={3}
            fullWidth
            value={editForm.description}
            onChange={handleEditChange("description")}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                fontSize: "13px",
              },
            }}
          />
          <CustomInputComponent
            label="Adresse"
            value={editForm.street}
            onChange={handleEditChange("street")}
            sx={{ width: "100%" }}
          />
          <CustomInputComponent
            label="Code postal"
            value={editForm.postalCode}
            onChange={handleEditChange("postalCode")}
            sx={{ width: "100%" }}
          />
          <CustomInputComponent
            label="Ville"
            value={editForm.city}
            onChange={handleEditChange("city")}
            sx={{ width: "100%" }}
          />
          <CustomInputComponent
            label="Pays"
            value={editForm.country}
            onChange={handleEditChange("country")}
            sx={{ width: "100%" }}
          />
          {data && isFieldEditable(data.type, "phone") && (
            <CustomInputComponent
              label="Téléphone"
              value={editForm.phone}
              onChange={handleEditChange("phone")}
              sx={{ width: "100%" }}
            />
          )}
          {data && isFieldEditable(data.type, "email") && (
            <CustomInputComponent
              label="Adresse e-mail"
              type="email"
              value={editForm.email}
              onChange={handleEditChange("email")}
              sx={{ width: "100%" }}
            />
          )}
          {data && isFieldEditable(data.type, "website") && (
            <CustomInputComponent
              label="Site web"
              value={editForm.website}
              onChange={handleEditChange("website")}
              sx={{ width: "100%" }}
            />
          )}
          {saveError && (
            <Typography fontSize="0.75rem" color="#c0392b">
              {saveError}
            </Typography>
          )}
        </Box>
      </CustomDialogComponent>

      {/* Delete Confirmation Dialog */}
      <CustomDialogComponent
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Supprimer l'élément"
        onConfirm={handleConfirmDelete}
        confirmLabel={
          deleteItemMutation.isPending ? "Suppression..." : "Supprimer"
        }
        cancelLabel="Annuler"
      >
        <Box display="flex" flexDirection="column" gap={1.5} py={1}>
          <Typography fontSize="0.85rem">
            Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est
            irréversible et supprimera définitivement ses images, ses
            équipements ainsi que toutes les réservations et avis associés.
          </Typography>
          {deleteError && (
            <Typography fontSize="0.75rem" color="#c0392b">
              {deleteError}
            </Typography>
          )}
        </Box>
      </CustomDialogComponent>
    </Box>
  );
};
